/* eslint-disable no-restricted-syntax */
/* eslint-disable operator-assignment */
/* eslint-disable no-bitwise */
/**
 * This is a class for a RandomSparseBitSet
 *
 * The class has the following fields:
 * 1. words - an array of rlong
 * 2. index - an array of int
 * 3. limit - an rint
 * 4. mask - an array of long
 */
export class RSparseBitSet {
  constructor() {
    this.words = [];
    this.initialMask = []; // data structure to store the initial state of the words array
    // indices of applicants, left of limit is removed, right of limit is active
    this.index = [];
    this.limit = -1;
    this.mask = [];
    // map applicant to a unique integer value
    this.applicantMapping = new Map();
    // map unique integer value to applicant
    this.applicantIndexMapping = new Map();
    this.applicants = [];
    this.assignment = undefined;
  }

  /**
   * Assigns the given value to the current domain and stores it in a separate data structure.
   * @param {*} value - The value to be assigned.
   */
  assign(value) {
    this.assignment = value;
  }

  /**
   * Unassigns the current value from the domain and adds it back to the active applicants.
   */
  unassign() {
    if (this.assignment !== undefined) {
      this.removeApplicant(this.assignment);
      this.assignment = undefined;
    }
  }

  /**
   * Returns the current assignment.
   * @returns {*} - The current assignment.
   */
  getAssignment() {
    return this.assignment;
  }

  /**
   * Creates a mapping of applicants to integer values.
   * @param {Applicant[]} applicants - An array of applicant objects.
   */
  createMapping(applicants) {
    let i = 0;
    for (const applicant of applicants) {
      this.applicantMapping.set(applicant, i);
      this.applicantIndexMapping.set(i, applicant);
      i++;
    }
    this.applicants = applicants;
  }

  getApplicants() {
    return this.applicants;
  }

  getActiveApplicants() {
    const activeApplicants = [];
    const activeIndexes = this.getActiveIndexes().sort((a, b) => (a < b ? -1 : 1));
    for (let i = 0; i < activeIndexes.length; i++) {
      activeApplicants.push(this.applicantIndexMapping.get(activeIndexes[i]));
    }
    return activeApplicants.filter(applicant => applicant !== undefined);
  }

  getActiveIndexes() {
    const activeIndexes = [];
    for (let i = 0; i <= this.limit; i++) {
      activeIndexes.push(this.index[i]);
    }
    return activeIndexes;
  }

  /**
   * Gets the next active applicant in the RSparseBitSet
   * @returns {Applicant|null} - The next active applicant or null if none are found
   */
  getNextApplicant() {
    for (let i = 0; i <= this.limit; i++) {
      const applicantIndex = this.index[i];
      const wordIndex = Math.floor(applicantIndex / 32);
      const bitIndex = 31 - (applicantIndex % 32);
      const bit = 1 << bitIndex;
      if (this.words[wordIndex] & bit) {
        return this.applicantIndexMapping.get(applicantIndex);
      }
    }
    return null;
  }

  /**
   * Initializes the RSparseBitSet with the given list of applicants.
   * @param {Applicant[]} applicants - An array of applicant objects.
   */
  initializeRSparseBitSet(applicants) {
    applicants.sort((a, b) => (a.vName < b.vName ? -1 : 1));
    // console.log(applicants);
    this.createMapping(applicants);
    const { length } = applicants;

    // Calculate the number of words required to store all domain values
    const numWords = Math.ceil(length / 32);

    // Initialize words, index, and mask arrays
    for (let i = 0; i < numWords; i++) {
      const remainingValues = length - i * 32;
      const initialWord = remainingValues >= 32
        ? 0xffffffff
        : ((1 << remainingValues) - 1) << (32 - remainingValues);
      this.words.push(initialWord);
      this.initialMask.push(initialWord); // Store the initial state of the words array
      this.mask.push(0);
    }

    for (let i = 0; i < length; i++) {
      this.index.push(i);
    }

    this.limit = length - 1;
  }

  /**
   * Resets the domain to its original state by reinstating all values that were removed.
   */
  reset() {
    this.words = [...this.initialMask]; // Set the words array back to its initial state
    this.limit = this.applicants.length - 1; // Restore the limit to its initial value
  }

  /**
   * Returns the integer value corresponding to the given applicant
   * based on the created mapping.
   * @param {Applicant} applicant - The applicant object to get the value for.
   * @return {number} - The integer value corresponding to the applicant.
   */
  getApplicantValue(applicant) {
    return this.applicantMapping.get(applicant);
  }

  /**
   * Removes a specific applicant value from the RSparseBitSet.
   * @param {Applicant} applicant - The applicant object to remove.
   */
  removeApplicant(applicant) {
    const value = this.getApplicantValue(applicant);
    if (value === undefined) return;

    const wordIndex = Math.floor(value / 32);
    const bitIndex = 31 - (value % 32);
    const offset = this.index.indexOf(value);

    if (offset > this.limit) return;

    const bit = 1 << bitIndex;
    this.words[wordIndex] = this.words[wordIndex] & ~bit;

    // Swap the removed value to the end of the active domain values in the index array
    this.index[offset] = this.index[this.limit];
    this.index[this.limit] = value;
    this.limit--;
  }

  /**
   * Adds a specific applicant value to the RSparseBitSet to restore domain values.
   * @param {Applicant} applicant - The applicant object to add.
   */
  addApplicant(applicant) {
    const value = this.getApplicantValue(applicant);
    if (value === undefined) return;

    const wordIndex = Math.floor(value / 32);
    const bitIndex = 31 - (value % 32);
    const offset = this.index.indexOf(value);

    // if (offset <= this.limit && this.index[offset] === value) return;

    const bit = 1 << bitIndex;
    this.words[wordIndex] = this.words[wordIndex] | bit;

    // Swap the added value to the end of the removed domain values in the index array
    this.index[offset] = this.index[this.limit + 1];
    this.index[this.limit + 1] = value;
    this.limit++;
  }

  /**
   * Adds all the applicants values to the RSparseBitSet to restore domain values.
   * @param {Applicant[]} applicants - The list of applicants to add.
   */
  addApplicants(applicants) {
    applicants.forEach((applicant) => this.addApplicant(applicant));
  }

  /**
   * Method isEmpty(): Boolean
   * Returns true if limit is -1 and false otherwise.
   * @return {boolean}
   */
  isEmpty() {
    return this.limit === -1;
  }

  /**
   * Method clearMask()
   * Sets the elements in the mask array to 0.
   */
  clearMask() {
    for (let i = 0; i <= this.limit; i++) {
      const offset = this.index[i];
      this.mask[offset] = 0;
    }
  }

  /**
   * Method reverseMask()
   * Performs a bitwise NOT on the elements in the mask array.
   */
  reverseMask() {
    for (let i = 0; i <= this.limit; i++) {
      const offset = this.index[i];
      this.mask[offset] = ~this.mask[offset];
    }
  }

  /**
   * Method addToMask(m: array of long)
   * Performs a bitwise OR operation on the elements in the mask array and the given array.
   * @param {array} m - an array of long
   */
  addToMask(m) {
    for (let i = 0; i <= this.limit; i++) {
      const offset = this.index[i];
      this.mask[offset] = this.mask[offset] | m[offset];
    }
  }

  /**
   * Method intersectWithMask()
   * Iterates through the words array and performs a bitwise AND operation with the mask array.
   * If the result of the operation is not equal to the value of the word, the word is set to the result of the operation.
   * If the result is equal to 0, the index of the word is moved to the end of the index array and the limit is decreased by 1.
   */
  intersectWithMask() {
    for (let i = this.limit; i >= 0; i--) {
      const offset = this.index[i];
      const w = this.words[offset] & this.mask[offset];
      if (w !== this.words[offset]) {
        this.words[offset] = w;
        if (w === 0) {
          this.index[i] = this.index[this.limit];
          this.index[this.limit] = offset;
          this.limit--;
        }
      }
    }
  }

  /**
   * Method intersectIndex(m: array of long): int
   * Iterates through the index array and performs a bitwise AND operation with the given array.
   * If the result of the operation is not equal to 0, the offset is returned.
   * @param {array} m - an array of long
   * @return {int} - the offset of the word where the bit-set intersects with m, -1 otherwise
   */
  intersectIndex(m) {
    for (let i = 0; i <= this.limit; i++) {
      const offset = this.index[i];
      if (this.words[offset] & (m[offset] !== 0)) {
        return offset;
      }
    }
    return -1;
  }

  getWords() {
    return this.words;
  }

  printWords() {
    console.log(
      'Words (Binary):',
      this.words.map((word) => (word >>> 0).toString(2).padStart(32, '0'))
    );
    console.log('Index:', this.index);
    console.log('Limit:', this.limit);
  }
}
